import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Eye, Pencil } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { sortByColumn, useTableSort } from "@/shared/hooks/useTableSort";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TableToolbar } from "../components/TableToolbar";
import { EmployeeAvatar } from "../components/EmployeeAvatar";
import { SortableTableHead } from "../components/SortableTableHead";
import { StatusBadge } from "../components/StatusBadge";
import { TablePagination } from "../components/TablePagination";
import { useEnterpriseEmployees } from "../hooks/useEnterpriseEmployees";
import type { Employee } from "../types";

function getEmployeeRecordId(employee: Employee): number | null {
  if (employee.recordId) return employee.recordId;
  const match = employee.id.match(/^emp-(\d+)$/);
  return match ? Number(match[1]) : null;
}

export function EnterpriseEmployeesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.employees.enterpriseList;
  const pageTitle = t.safecheck.nav.employeeList;
  const { employees, loading, error } = useEnterpriseEmployees();

  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const { sortKey, sortDirection, toggleSort } = useTableSort("name", "asc");

  const handleSort = (key: string) => {
    toggleSort(key);
    setPage(1);
  };

  const filtered = useMemo(() => {
    return employees.filter((employee) => {
      if (!tableSearch) return true;
      const haystack =
        `${employee.name} ${employee.systemId} ${employee.companyMatricule} ${employee.branch} ${employee.province}`.toLowerCase();
      return haystack.includes(tableSearch.toLowerCase());
    });
  }, [employees, tableSearch]);

  const sorted = useMemo(
    () =>
      sortByColumn(filtered, sortKey, sortDirection, (employee, key) => {
        if (key === "name") return employee.name;
        if (key === "systemId") return employee.systemId;
        if (key === "matricule") return employee.companyMatricule;
        if (key === "branch") return employee.branch;
        if (key === "province") return employee.province;
        if (key === "status") return labels.statuses[employee.status];
        return "";
      }),
    [filtered, sortKey, sortDirection, labels.statuses],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const columnDefs = [
    { key: "photo", label: labels.columns.photo, sortable: false },
    { key: "name", label: labels.columns.name, sortable: true },
    { key: "systemId", label: labels.columns.systemId, sortable: true },
    { key: "matricule", label: labels.columns.matricule, sortable: true },
    { key: "branch", label: labels.columns.branch, sortable: true },
    { key: "province", label: labels.columns.province, sortable: true },
    { key: "status", label: labels.columns.status, sortable: true },
    { key: "actions", label: "Actions", sortable: false },
  ] as const;

  return (
    <>
      <Breadcrumbs
        homeHref="/dashboard/enterprise"
        items={[
          { label: t.safecheck.nav.employeeManagement },
          { label: pageTitle },
        ]}
      />

      {error && (
        <p className="mb-3 rounded-xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}
      {loading && <p className="mb-3 text-sm text-muted">Chargement des employés…</p>}

      <div className="theme-card">
        <div className="border-b border-border px-5 py-4">
          <h1 className="text-base font-semibold text-foreground">{pageTitle}</h1>
        </div>

        <TableToolbar
          search={tableSearch}
          onSearchChange={(value) => {
            setTableSearch(value);
            setPage(1);
          }}
          searchPlaceholder={t.safecheck.search}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showLabel={t.safecheck.show}
          entriesLabel={t.safecheck.entries}
        />

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columnDefs.map((column) => (
                  <SortableTableHead
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                    sortable={column.sortable}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((employee) => {
                const recordId = getEmployeeRecordId(employee);
                return (
                <tr
                  key={employee.id}
                  className="border-b border-border last:border-0 hover:bg-card-muted/30"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <ChevronRight size={14} className="text-muted" />
                      <EmployeeAvatar name={employee.name} photo={employee.photo} size={36} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-foreground">
                    {recordId ? (
                      <Link
                        to={`/dashboard/employees/${recordId}`}
                        className="text-primary transition-colors hover:text-primary/80"
                      >
                        {employee.name}
                      </Link>
                    ) : (
                      employee.name
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge variant="purple">{employee.systemId}</StatusBadge>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{employee.companyMatricule}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{employee.branch}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{employee.province}</td>
                  <td className="px-4 py-3">
                    <StatusBadge variant={employee.status === "active" ? "success" : "neutral"}>
                      {labels.statuses[employee.status]}
                    </StatusBadge>
                  </td>
                  <td className="px-4 py-3">
                    {recordId ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/dashboard/employees/${recordId}`}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-card-muted"
                        >
                          <Eye size={14} />
                          Voir
                        </Link>
                        <Link
                          to={`/dashboard/employees/${recordId}/edit`}
                          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                        >
                          <Pencil size={14} />
                          Modifier
                        </Link>
                      </div>
                    ) : (
                      <span className="text-xs text-muted">—</span>
                    )}
                  </td>
                </tr>
              );
              })}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={sorted.length}
          onPageChange={handlePageChange}
          labels={labels.pagination}
        />
      </div>
    </>
  );
}
